// src/components/orders/DesktopOrdersView.js
import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import Info from '@mui/icons-material/Info';

const DesktopOrdersView = ({ orders, getOrderItemsSummary, getStatusColor, getStatusLabel }) => {
  const navigate = useNavigate();

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending':
        return <AccessTimeIcon sx={{ fontSize: 16 }} />;
      case 'confirmed':
        return <ThumbUpIcon sx={{ fontSize: 16 }} />;
      case 'shipped':
        return <LocalShippingIcon sx={{ fontSize: 16 }} />;
      case 'delivered':
        return <CheckCircleOutlineIcon sx={{ fontSize: 16 }} />;
      case 'cancelled':
        return <HighlightOffIcon sx={{ fontSize: 16 }} />;
      default:
        return null;
    }
  };

  // Helper function to get product names without quantities
  const getProductNamesOnly = (order) => {
    if (!order.order_items || order.order_items.length === 0) {
      return 'لا توجد منتجات';
    }
    
    const productNames = order.order_items.map(item => item.product_name || item.name);
    
    if (productNames.length <= 2) {
      return productNames.join('، ');
    } else {
      return `${productNames.slice(0, 2).join('، ')} و ${productNames.length - 2} منتج آخر`;
    }
  };

  return (
    <TableContainer 
      sx={{ 
        direction: 'rtl',
        '& .MuiTable-root': {
          direction: 'rtl',
          minWidth: 650,
          borderCollapse: 'separate',
          borderSpacing: 0
        },
        // Enhanced mobile responsiveness
        '@media (max-width: 900px)': {
          '& .MuiTable-root': {
            minWidth: 'auto'
          }
        },
        // Horizontal scroll on very small screens if needed
        overflowX: 'auto'
      }}
    >
      <Table sx={{ direction: 'rtl' }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{
              textAlign: 'center',
              direction: 'rtl',
              padding: '16px 24px',
              fontWeight: 'bold',
              color: 'white',
              backgroundColor: 'primary.main',
              borderTopRightRadius: '8px',
              '@media (max-width: 900px)': { padding: '12px 8px', fontSize: '0.875rem' },
              '@media (max-width: 600px)': { padding: '8px 4px', fontSize: '0.8rem' }
            }}>
              رقم الطلب
            </TableCell>
            <TableCell sx={{
              textAlign: 'center',
              direction: 'rtl',
              padding: '16px 24px',
              fontWeight: 'bold',
              color: 'white',
              backgroundColor: 'primary.main',
              borderRight: '1px solid rgba(255, 255, 255, 0.2)',
              '@media (max-width: 900px)': { padding: '12px 8px', fontSize: '0.875rem' },
              '@media (max-width: 600px)': { padding: '8px 4px', fontSize: '0.8rem' }
            }}>
              التاريخ
            </TableCell>
            <TableCell sx={{
              textAlign: 'center',
              direction: 'rtl',
              padding: '16px 24px',
              fontWeight: 'bold',
              color: 'white',
              backgroundColor: 'primary.main',
              borderRight: '1px solid rgba(255, 255, 255, 0.2)',
              '@media (max-width: 900px)': { padding: '12px 8px', fontSize: '0.875rem' },
              '@media (max-width: 600px)': { padding: '8px 4px', fontSize: '0.8rem' }
            }}>
              المنتجات
            </TableCell>
            <TableCell sx={{
              textAlign: 'center',
              direction: 'rtl',
              padding: '16px 24px',
              fontWeight: 'bold',
              color: 'white',
              backgroundColor: 'primary.main',
              borderRight: '1px solid rgba(255, 255, 255, 0.2)',
              '@media (max-width: 900px)': { padding: '12px 8px', fontSize: '0.875rem' },
              '@media (max-width: 600px)': { padding: '8px 4px', fontSize: '0.8rem' }
            }}>
              الكمية
            </TableCell>
            <TableCell sx={{
              textAlign: 'center',
              direction: 'rtl',
              padding: '16px 24px',
              fontWeight: 'bold',
              color: 'white',
              backgroundColor: 'primary.main',
              borderRight: '1px solid rgba(255, 255, 255, 0.2)',
              '@media (max-width: 900px)': { padding: '12px 8px', fontSize: '0.875rem' },
              '@media (max-width: 600px)': { padding: '8px 4px', fontSize: '0.8rem' }
            }}>
              المبلغ
            </TableCell>
            <TableCell sx={{
              textAlign: 'center',
              direction: 'rtl',
              padding: '16px 24px',
              fontWeight: 'bold',
              color: 'white',
              backgroundColor: 'primary.main',
              borderRight: '1px solid rgba(255, 255, 255, 0.2)',
              '@media (max-width: 900px)': { padding: '12px 8px', fontSize: '0.875rem' },
              '@media (max-width: 600px)': { padding: '8px 4px', fontSize: '0.8rem' }
            }}>
              الحالة
            </TableCell>
            <TableCell sx={{
              textAlign: 'center',
              direction: 'rtl',
              padding: '16px 24px',
              fontWeight: 'bold',
              color: 'white',
              backgroundColor: 'primary.main',
              borderTopLeftRadius: '8px',
              borderRight: '1px solid rgba(255, 255, 255, 0.2)',
              '@media (max-width: 900px)': { padding: '12px 8px', fontSize: '0.875rem' },
              '@media (max-width: 600px)': { padding: '8px 4px', fontSize: '0.8rem' }
            }}>
              التفاصيل
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {orders.map((order, idx) => (
            <TableRow key={order.id} hover>
              <TableCell sx={{
                textAlign: 'center',
                direction: 'rtl',
                padding: '16px 24px',
                '@media (max-width: 900px)': { padding: '12px 8px', fontSize: '0.875rem' },
                '@media (max-width: 600px)': { padding: '8px 4px', fontSize: '0.8rem' }
              }}>
                <Typography variant="body2" sx={{ 
                  fontWeight: 600,
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  color: 'primary.main'
                }}>
                  #{order.id.slice(0, 8)}
                </Typography>
              </TableCell>
              <TableCell sx={{
                textAlign: 'center',
                direction: 'rtl',
                padding: '16px 24px',
                borderRight: '1px solid rgba(224, 224, 224, 1)',
                '@media (max-width: 900px)': { padding: '12px 8px', fontSize: '0.875rem' },
                '@media (max-width: 600px)': { padding: '8px 4px', fontSize: '0.8rem' }
              }}>
                <Typography variant="body2" sx={{ 
                  fontWeight: 'bold',
                  fontSize: { xs: '0.75rem', sm: '0.875rem' }
                }}>
                  {new Date(order.created_at).toLocaleDateString('ar-EG', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </Typography>
              </TableCell>
              <TableCell sx={{
                textAlign: 'center',
                direction: 'rtl',
                padding: '16px 24px',
                borderRight: '1px solid rgba(224, 224, 224, 1)',
                '@media (max-width: 900px)': { padding: '12px 8px', fontSize: '0.875rem' },
                '@media (max-width: 600px)': { padding: '8px 4px', fontSize: '0.8rem' }
              }}>
                <Typography variant="body2" sx={{ 
                  fontWeight: 600,
                  fontSize: { xs: '0.75rem', sm: '0.875rem' }
                }}>
                  {getProductNamesOnly(order)}
                </Typography>
              </TableCell>
              <TableCell sx={{
                textAlign: 'center',
                direction: 'rtl',
                padding: '16px 24px',
                borderRight: '1px solid rgba(224, 224, 224, 1)',
                '@media (max-width: 900px)': { padding: '12px 8px', fontSize: '0.875rem' },
                '@media (max-width: 600px)': { padding: '8px 4px', fontSize: '0.8rem' }
              }}>
                <Typography variant="body2" sx={{ 
                  fontWeight: 600,
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  color: 'info.main'
                }}>
                  {order.order_items?.reduce((total, item) => total + item.quantity, 0) || 0}
                </Typography>
              </TableCell>
              <TableCell sx={{
                textAlign: 'center',
                direction: 'rtl',
                padding: '16px 24px',
                borderRight: '1px solid rgba(224, 224, 224, 1)',
                '@media (max-width: 900px)': { padding: '12px 8px', fontSize: '0.875rem' },
                '@media (max-width: 600px)': { padding: '8px 4px', fontSize: '0.8rem' }
              }}>
                <Typography variant="body2" sx={{ 
                  fontWeight: 700,
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  color: 'success.main'
                }}>
                  {order.total_amount.toFixed(2)} جنيه
                </Typography>
              </TableCell>
              <TableCell sx={{
                textAlign: 'center',
                direction: 'rtl',
                padding: '16px 24px',
                borderRight: '1px solid rgba(224, 224, 224, 1)',
                '@media (max-width: 900px)': { padding: '12px 8px', fontSize: '0.875rem' },
                '@media (max-width: 600px)': { padding: '8px 4px', fontSize: '0.8rem' }
              }}>
                <Chip
                  icon={getStatusIcon(order.status)}
                  label={getStatusLabel(order.status)}
                  color={getStatusColor(order.status)}
                  size="small"
                  sx={{ 
                    fontWeight: 'bold',
                    fontSize: { xs: '0.65rem', sm: '0.75rem' },
                    height: { xs: 20, sm: 24 },
                    minWidth: '120px',
                    '& .MuiChip-icon': {
                      color: 'inherit'
                    }
                  }}
                />
              </TableCell>
              <TableCell sx={{
                textAlign: 'center',
                direction: 'rtl',
                padding: '16px 24px',
                borderRight: '1px solid rgba(224, 224, 224, 1)',
                '@media (max-width: 900px)': { padding: '12px 8px', fontSize: '0.875rem' },
                '@media (max-width: 600px)': { padding: '8px 4px', fontSize: '0.8rem' }
              }}>
                <Button 
                  variant="outlined" 
                  size="small"
                  onClick={() => navigate(`/order/${order.id}`)}
                  startIcon={<Info />}
                  sx={{
                    borderRadius: 2,
                    fontWeight: 'bold',
                    fontSize: { xs: '0.65rem', sm: '0.75rem' },
                    minWidth: 'auto',
                    padding: '4px 12px',
                    textTransform: 'none',
                    '& .MuiButton-startIcon': {
                      marginRight: '4px',
                      marginLeft: 1
                    }
                  }}
                >
                  عرض التفاصيل
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default DesktopOrdersView;